import type { MouseEvent, ReactNode } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { AlertTriangle, Info } from "lucide-react";

export type ConfirmAlertDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  /** confirm = Cancel + primary action; alert = single OK (SweetAlert-style info) */
  mode?: "confirm" | "alert";
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: (event: MouseEvent<HTMLButtonElement>) => void;
  variant?: "danger" | "warning";
  contentClassName?: string;
};

export function ConfirmAlertDialog({
  open,
  onOpenChange,
  title,
  description,
  mode = "confirm",
  confirmLabel,
  cancelLabel = "Cancel",
  onConfirm,
  variant = "danger",
  contentClassName,
}: ConfirmAlertDialogProps) {
  const isDanger = variant === "danger";
  const Icon = isDanger ? AlertTriangle : Info;
  const defaultConfirm = mode === "alert" ? "OK" : isDanger ? "Delete" : "Confirm";
  const confirmText = confirmLabel ?? defaultConfirm;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={cn("sm:max-w-md border-border shadow-xl", contentClassName)}>
        <AlertDialogHeader className="sm:text-left space-y-0">
          <div className="flex gap-4 sm:gap-5">
            <div
              className={cn(
                "flex h-12 w-12 shrink-0 items-center justify-center rounded-full",
                isDanger
                  ? "bg-destructive/10 text-destructive"
                  : "bg-amber-500/15 text-amber-600 dark:text-amber-400",
              )}
            >
              <Icon className="h-6 w-6" strokeWidth={2} />
            </div>
            <div className="space-y-2 min-w-0 flex-1 pt-0.5">
              <AlertDialogTitle className="text-xl font-display font-semibold tracking-tight text-left">
                {title}
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="text-sm text-muted-foreground leading-relaxed text-left">{description}</div>
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          {mode === "confirm" ? (
            <>
              <AlertDialogCancel className="mt-0 border-border">{cancelLabel}</AlertDialogCancel>
              <AlertDialogAction
                className={cn(
                  buttonVariants(),
                  isDanger &&
                    "bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive",
                )}
                onClick={onConfirm}
              >
                {confirmText}
              </AlertDialogAction>
            </>
          ) : (
            <AlertDialogAction
              className={cn(buttonVariants({ variant: "default" }), "w-full sm:w-auto sm:min-w-[7rem]")}
              onClick={onConfirm}
            >
              {confirmText}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
